<template>
  <div class="ei-growth-profile">
    <h2 class="ei-growth-profile__title">
      ЭИ-профиль с сильными сторонами и зонами роста
    </h2>

    <div class="ei-growth-profile__grid">
      <a-card class="ei-growth-profile__card">
        <template #title>
          <div class="ei-growth-profile__card-title">
            <span>Средний ЭИ-профиль</span>

            <a-popover
                v-if="averageHelpText"
                trigger="hover"
                placement="top"
            >
              <template #content>
                <div class="ei-growth-profile__popover">
                  {{ averageHelpText }}
                </div>
              </template>

              <QuestionCircleOutlined class="ei-growth-profile__help-icon" />
            </a-popover>
          </div>
        </template>

        <div class="ei-growth-profile__legend">
          <div
              v-for="item in legendItems"
              :key="item.key"
              class="ei-growth-profile__legend-item"
          >
            <span
                class="ei-growth-profile__legend-mark"
                :style="{ backgroundColor: item.color }"
            ></span>

            <span>{{ item.label }}</span>
          </div>
        </div>

        <EiScaleRowsTable
            class="ei-growth-profile__scale-table ei-growth-profile__scale-table--delta-only"
            :sections="averageProfileSections"
            mode="compare"
            :show-track-values="true"
            :show-difference="true"
            :show-scale-after-each-section="true"
            :show-first-section-title="false"
            :default-color="mainColor"
            :default-compare-color="compareColor"
            :scale-height="12"
            :compare-scale-height="12"
            :row-gap="12"
        />
      </a-card>

      <a-card class="ei-growth-profile__card">
        <template #title>
          <div class="ei-growth-profile__card-title">
            <span>Детализация ЭИ (10 разделов способностей)</span>

            <a-popover
                v-if="detailsHelpText"
                trigger="hover"
                placement="top"
            >
              <template #content>
                <div class="ei-growth-profile__popover">
                  {{ detailsHelpText }}
                </div>
              </template>

              <QuestionCircleOutlined class="ei-growth-profile__help-icon" />
            </a-popover>
          </div>
        </template>

        <div class="ei-growth-profile__legend">
          <div
              v-for="item in legendItems"
              :key="item.key"
              class="ei-growth-profile__legend-item"
          >
            <span
                class="ei-growth-profile__legend-mark"
                :style="{ backgroundColor: item.color }"
            ></span>

            <span>{{ item.label }}</span>
          </div>
        </div>

        <EiScaleRowsTable
            class="ei-growth-profile__scale-table ei-growth-profile__scale-table--delta-only"
            :sections="detailsSections"
            mode="compare"
            :show-track-values="true"
            :show-difference="true"
            :show-scale-after-each-section="false"
            :show-first-section-title="true"
            :default-color="mainColor"
            :default-compare-color="compareColor"
            :scale-height="12"
            :compare-scale-height="12"
            :row-gap="12"
        />
      </a-card>
    </div>

    <div class="ei-growth-profile__footer" data-html2canvas-ignore>
      <a-button class="secondary-button" @click="scrollTop">
        Наверх
      </a-button>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue'
import { QuestionCircleOutlined } from '@ant-design/icons-vue'
import EiScaleRowsTable from "@/components/Analytics/EiScaleRowsTable.vue";
import cards from "@/views/Report/ParametersCards/parametersCardsList";

export default {
  name: 'EiGrowthProfileBlock',

  components: {
    EiScaleRowsTable,
    QuestionCircleOutlined,
  },

  props: {
    scores: {
      type: Object,
      default: () => ({}),
    },

    mainLegendLabel: {
      type: String,
      default: 'Руководители проектов',
    },

    compareLegendLabel: {
      type: String,
      default: 'Отраслевой ЭИ-профиль',
    },

    mainColor: {
      type: String,
      default: '#8EA8F8',
    },

    compareColor: {
      type: String,
      default: '#D4D9E2',
    },
  },

  setup(props) {
    const averageHelpText = 'Сравнение общего ЭИ-профиля, доменов и ветвей с выбранным эталонным профилем.';
    const detailsHelpText = 'Сравнение десяти разделов способностей ЭИ с выбранным эталонным профилем.';

    const averageKeys = [
      'total',
      'domain_1',
      'domain_2',
      'branch_1',
      'branch_2',
      'branch_3',
      'branch_4',
    ]

    const detailsKeys = [
      'section_1',
      'section_5',
      'section_9a',
      'section_2',
      'section_6',
      'section_3',
      'section_7',
      'section_9b',
      'section_4',
      'section_8',
    ]

    const legendItems = computed(() => [
      {
        key: 'compare',
        label: props.compareLegendLabel,
        color: props.compareColor,
      },
      {
        key: 'main',
        label: props.mainLegendLabel,
        color: props.mainColor,
      },
    ])

    const getCard = (key) => {
      return cards instanceof Map
          ? cards.get(key)
          : cards[key]
    }

    const toNumber = (value, fallback = 0) => {
      const number = Number(value)

      return Number.isFinite(number) ? number : fallback;
    }

    const getScore = (key) => {
      return props.scores[key] || {}
    }

    const getDifferenceColor = (difference) => {
      if (difference > 0) {
        return '#21AC0C';
      }

      if (difference < 0) {
        return '#FF4D4F';
      }

      return 'rgba(0, 0, 0, 0.35)';
    }

    const buildRow = (key) => {
      const card = getCard(key) || {};
      const score = getScore(key);

      const value = toNumber(score.value);
      const compareValue = toNumber(score.compareValue);
      const difference = score.difference !== undefined && score.difference !== null
          ? toNumber(score.difference)
          : value - compareValue;

      return {
        key,
        label: card.title || key,
        helpText: card.descriptionContent || '',
        value,
        compareValue,
        difference,
        color: props.mainColor,
        compareColor: props.compareColor,
        differenceColor: getDifferenceColor(difference),
      }
    }

    const getRowsByKeys = (keys) => {
      return keys.map((key) => buildRow(key))
    }

    const averageProfileSections = computed(() => [
      {
        key: 'general',
        title: '',
        rows: getRowsByKeys(['total']),
      },
      {
        key: 'domains',
        title: 'Домены',
        rows: getRowsByKeys(['domain_1', 'domain_2']),
      },
      {
        key: 'branches',
        title: 'Ветви',
        rows: getRowsByKeys(['branch_1', 'branch_2', 'branch_3', 'branch_4']),
      },
    ])

    const detailsSections = computed(() => [
      {
        key: 'sections',
        title: 'Разделы',
        rows: getRowsByKeys(detailsKeys),
      },
    ])

    const scrollTop = () => {
      const container = document.querySelector('.page-container')

      if (container) {
        container.scrollTo({
          top: 0,
          behavior: 'smooth',
        })

        return
      }

      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }

    return {
      averageKeys,
      detailsKeys,
      legendItems,
      averageHelpText,
      detailsHelpText,
      averageProfileSections,
      detailsSections,
      scrollTop,
    }
  },
}
</script>

<style scoped lang="scss">
.ei-growth-profile {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  text-align: start;
}

.ei-growth-profile__title {
  margin: 0 0 16px;
  font-weight: 600;
  font-size: 16px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.85);
}

.ei-growth-profile__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  align-items: start;
  width: 100%;
  min-width: 0;
  max-width: 100%;
}

.ei-growth-profile__card {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  border-radius: 6px;
}

.ei-growth-profile__card :deep(.ant-card-head) {
  min-height: auto;
  padding: 0 20px;
  border-bottom: none;
}

.ei-growth-profile__card :deep(.ant-card-head-title) {
  padding: 20px 0 8px;
}

.ei-growth-profile__card :deep(.ant-card-body) {
  padding: 0 20px 20px;
}

.ei-growth-profile__card-title {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 16px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.ei-growth-profile__help-icon {
  flex: 0 0 auto;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.25);
  cursor: pointer;
}

.ei-growth-profile__popover {
  max-width: 260px;
  font-size: 13px;
  line-height: 130%;
}

.ei-growth-profile__legend {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
  margin: 0 0 16px;
}

.ei-growth-profile__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);

}

.ei-growth-profile__legend-mark {
  width: 10px;
  height: 10px;
  border-radius: 2px;
}

.ei-growth-profile__scale-table {
  width: 100%;
}

.ei-growth-profile__scale-table--delta-only :deep(.ei-scale-table__value) {
  display: none;
}

.ei-growth-profile__scale-table--delta-only :deep(.ei-scale-table__difference) {
  min-width: 28px;
}

.ei-growth-profile__scale-table--delta-only :deep(.ei-scale-table__difference--positive) {
  color: #21AC0C;
}

.ei-growth-profile__scale-table--delta-only :deep(.ei-scale-table__difference--negative) {
  color: #FF4D4F;
}

.ei-growth-profile__scale-table--delta-only :deep(.ei-scale-table__difference--neutral) {
  color: rgba(0, 0, 0, 0.35);
}

.ei-growth-profile__scale-table :deep(.ei-scale-table__row-header) {
  font-size: 14px;
  margin-bottom: 8px;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
}

.ei-growth-profile__scale-table :deep(.ei-scale-table__section-title) {
  margin-bottom: 8px;
  font-size: 14px;
}

.ei-growth-profile__scale-table :deep(.ei-scale-table__track) {
  background-color: #EEF1F5;
}

.ei-growth-profile__scale-table :deep(.ei-scale-table__track-value) {
  font-size: 11px;
  font-weight: 500;
}

.ei-growth-profile__scale-table :deep(.ei-scale-table__scale) {
  margin-top: 6px;
  font-size: 11px;
}

.ei-growth-profile__footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 28px;
}

@media (max-width: $tablet-breakpoint) {
  .ei-growth-profile__grid {
    grid-template-columns: 1fr;
  }
}
</style>